import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { toast } from 'sonner@2.0.3';
import { partnerApi, type PartnerReportRow } from '../../shared/api/partner.api';
import { num, postDay } from '../partner/period';
import { DateRangeSelector } from '../../features/statistics/components/DateRangeSelector';
import { useDateRangeParams } from '../../features/statistics/hooks/useDateRangeParams';
import { periodDays } from '../../features/statistics/lib/date-range';

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * «Статистика → Партнёры» (25.10.2026, Жулдыз): результаты партнёров без
 * ручного запроса статистики — общий итог и каждый партнёр отдельно.
 * Публикации и просмотры — из ночного снимка подключённых аккаунтов.
 * Фильтры — по периоду (общему для раздела), числу публикаций и просмотрам.
 */
export function PartnersTab() {
  const [periodOption, setPeriodOption] = useDateRangeParams();
  const { from: fromDay, to: toDay } = periodDays(periodOption);
  const from = day(fromDay);
  const to = day(toDay);
  const [data, setData] = useState<{ totals: { partners: number; posts: number; views: number | null }; partners: PartnerReportRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minPosts, setMinPosts] = useState('');
  const [minViews, setMinViews] = useState('');
  const [sort, setSort] = useState<'views' | 'posts'>('views');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    partnerApi.report(from, to)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) { setError(e.message); toast.error(e.message); } });
    return () => { cancelled = true; };
  }, [from, to]);

  const rows = useMemo(() => (data?.partners ?? [])
    .filter((p) => p.posts >= Number(minPosts || 0) && (p.views ?? 0) >= Number(minViews || 0))
    .sort((a, b) => (sort === 'posts' ? b.posts - a.posts : (b.views ?? 0) - (a.views ?? 0))), [data, minPosts, minViews, sort]);
  const posts = rows.reduce((n, p) => n + p.posts, 0);
  const views = rows.some((p) => p.views != null) ? rows.reduce((n, p) => n + (p.views ?? 0), 0) : null;
  const field = 'h-9 w-28 rounded-md border bg-background px-3 text-sm';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeSelector value={periodOption} onChange={setPeriodOption} />
        <span className="flex-1" />
        <input inputMode="numeric" value={minPosts} onChange={(e) => setMinPosts(e.target.value.replace(/\D/g, ''))} placeholder="публ. от" className={field} aria-label="Публикаций не меньше" />
        <input inputMode="numeric" value={minViews} onChange={(e) => setMinViews(e.target.value.replace(/\D/g, ''))} placeholder="просм. от" className={field} aria-label="Просмотров не меньше" />
        <select value={sort} onChange={(e) => setSort(e.target.value as 'views' | 'posts')} className="h-9 rounded-md border bg-background px-3 text-sm" aria-label="Сортировка">
          <option value="views">по просмотрам</option>
          <option value="posts">по публикациям</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[['Партнёров', rows.length], ['Публикаций', posts], ['Просмотров', views]].map(([name, value]) => (
          <div key={name as string} className="rounded-lg border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">{name}</div>
            <div className="text-xl font-semibold tabular-nums">{num(value as number | null)}</div>
          </div>
        ))}
      </div>

      {error ? (
        <p className="text-sm text-destructive">Не удалось загрузить: {error}</p>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Загружаем…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          {data.partners.length ? 'Под фильтр никто не подходит.' : 'Партнёров пока нет — пригласите их в «Сотрудниках → Партнёры».'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Партнёр</th>
                <th className="px-3 py-2.5 font-medium">Аккаунты</th>
                <th className="px-3 py-2.5 text-right font-medium">Публикаций</th>
                <th className="px-3 py-2.5 text-right font-medium">Просмотров</th>
                <th className="px-3 py-2.5 text-right font-medium">Среднее</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <PartnerRow key={p.id} row={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Публикации — по дате выхода; просмотры — последний ночной снимок. Новая публикация появляется на следующий день.</p>
    </div>
  );
}

function PartnerRow({ row: p, open, onToggle }: { row: PartnerReportRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="cursor-pointer border-b hover:bg-muted/30" onClick={onToggle}>
        <td className="px-4 py-2.5 font-medium">{p.name}<div className="text-xs font-normal text-muted-foreground">{p.login}</div></td>
        <td className="px-3 py-2.5 text-muted-foreground">{p.accounts.map((a) => `@${a.username}`).join(', ') || 'не подключены'}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{p.posts}</td>
        <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{num(p.views)}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{p.views != null && p.posts ? num(Math.round(p.views / p.posts)) : '—'}</td>
        <td className="px-2"><ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} /></td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/20">
          <td colSpan={6} className="px-4 py-2">
            {p.items.length === 0 ? (
              <span className="text-xs text-muted-foreground">За период публикаций нет.</span>
            ) : (
              <ul className="flex flex-col gap-1">
                {p.items.map((i) => (
                  <li key={i.mediaId} className="flex items-center gap-3 text-[13px]">
                    <span className="w-16 shrink-0 text-muted-foreground">{postDay(i.publishedAt)}</span>
                    <span className="w-40 shrink-0 truncate">@{i.account ?? '—'}</span>
                    <span className="w-28 shrink-0 text-right tabular-nums">{num(i.views)} просм.</span>
                    {i.link && <a href={i.link} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-1 truncate text-muted-foreground hover:text-foreground"><ExternalLink className="size-3.5 shrink-0" />{i.link.replace(/^https?:\/\/(www\.)?/, '')}</a>}
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
